import React, { useState, useEffect, useContext } from "react";
import {
  FaWallet,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaInfoCircle,
  FaSignOutAlt,
  FaShoppingBag,
  FaUser,
  FaBell,
  FaHeart,
  FaChevronDown,
  FaCheckCircle,
  FaEdit
} from "react-icons/fa";
import { PenLine, ShieldCheck, UserCheck, Smartphone, Mail, Sparkles, ChevronRight } from "lucide-react";

import Address from "./Address/Index";
import Wallet from "./Wallet/Wallet";
import FamilyMemberById from "./FamilyMember/FamilyMemberById";
import CreateFamilyMember from "./FamilyMember/CreateFamilyMember";
import MyOrders from "./Order/MyOrder";
import Wishlist from "./Wishlist/Wishlist";
import HelpSupport from "./HelpSupport/HelpSupport";

import UpdateProfileModal from "../../component/Navbar/UpdateProfileModal";
import LogoutPopup from "../../component/common/LogoutPopup";

// Context import
import { UserContext } from "../../context/UserContext";
import GetNotification from "./Notification/GetNotification";
import { useSearchParams, useLocation, Link } from "react-router-dom";

export default function Profile() {
  const { user, updateUser, logoutUser } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const [userData, setUserData] = useState(user || {});
  const [loading, setLoading] = useState(false);

  const pathname = location.pathname.toLowerCase();
  const isOrdersRoute = pathname === "/orders" || pathname === "/my-orders";
  const initialTab = isOrdersRoute
    ? "orders"
    : searchParams.get("tab") || location.state?.activeSection || "profile";

  const [activeSection, setActiveSection] = useState(initialTab);
  const [showCreateFamilyForm, setShowCreateFamilyForm] = useState(false);
  const [editMemberData, setEditMemberData] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [refreshFamilyList, setRefreshFamilyList] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const p = location.pathname.toLowerCase();
    if (p === "/orders" || p === "/my-orders") {
      setActiveSection("orders");
      return;
    }
    const tab = searchParams.get("tab");
    if (tab) {
      setActiveSection(tab);
    }
  }, [location.pathname, searchParams]);

  const userId = user?.id || user?.userId;

  const isEmailLogin = !!userData?.emailId && !userData?.phoneNumber;
  const isPhoneLogin = !!userData?.phoneNumber && !userData?.emailId;

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  const getImageUrl = () => {
    if (!userData?.photo) return null;
    if (userData.photo.startsWith("data:image")) return userData.photo;
    if (userData.photo.startsWith("http")) return `${userData.photo}?v=${Date.now()}`;
    return `https://admin.medrphalabs.com/CustomerImage/${userData.photo}?v=${Date.now()}`;
  };

  const confirmLogout = () => {
    logoutUser();
    setShowLogoutPopup(false);
    window.location.href = "/signin";
  };

  const handleProfileUpdate = (updatedData) => {
    updateUser(updatedData);
    setUserData(updatedData);
    setShowProfileModal(false);
  };

  const handleFamilyMemberUpdate = (updatedData) => {
    setShowCreateFamilyForm(false);
    setShowEditForm(false);
    setRefreshFamilyList(!refreshFamilyList);
  };

  const menuItems = [
    { id: "profile",        text: "Profile Overview",   icon: <FaInfoCircle size={17} />, badge: "Active" },
    { id: "orders",         text: "My Lab Bookings",     icon: <FaShoppingBag size={17} />, badge: "Reports" },
    { id: "wallet",         text: "Medrpha Wallet",     icon: <FaWallet size={17} /> },
    { id: "family_member",  text: "Family Members",      icon: <FaUser size={17} /> },
    { id: "address",        text: "Saved Addresses",    icon: <FaMapMarkerAlt size={17} /> },
    { id: "wishlist",       text: "Saved Tests",        icon: <FaHeart size={17} /> },
    { id: "notifications",  text: "Alerts & Updates",   icon: <FaBell size={17} /> },
    { id: "help_support",   text: "Help & Support",      icon: <FaQuestionCircle size={17} /> },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "wallet":       return <Wallet />;
      case "address":      return <Address />;
      case "orders":       return <MyOrders />;
      case "wishlist":     return <Wishlist />;
      case "help_support": return <HelpSupport />;
      case "notifications":return <GetNotification />;

      case "family_member":
        return (
          <>
            <FamilyMemberById
              userId={userId}
              onAdd={() => setShowCreateFamilyForm(true)}
              onEdit={(data) => { setEditMemberData(data); setShowEditForm(true); }}
              refresh={refreshFamilyList}
            />
            {showCreateFamilyForm && (
              <CreateFamilyMember
                userId={userId}
                onClose={() => setShowCreateFamilyForm(false)}
                onSuccess={handleFamilyMemberUpdate}
              />
            )}
            {showEditForm && (
              <CreateFamilyMember
                userId={userId}
                editData={editMemberData}
                onClose={() => setShowEditForm(false)}
                onSuccess={handleFamilyMemberUpdate}
              />
            )}
          </>
        );

      default:
        return (
          <div className="space-y-6">
            
            {/* Top Welcome Banner Card */}
            <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-2 backdrop-blur-xs">
                    <Sparkles size={12} className="text-amber-300" />
                    <span>Verified Patient Account</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
                    {userData?.customerName ? `Welcome back, ${userData.customerName}!` : "Welcome to Medrpha Labs"}
                  </h2>
                  <p className="text-xs sm:text-sm text-red-100 mt-1 max-w-lg">
                    Manage your health profiles, track doorstep sample collection, and view verified digital reports.
                  </p>
                </div>

                <button
                  onClick={() => setShowProfileModal(true)}
                  className="px-5 py-2.5 bg-white text-red-600 hover:bg-red-50 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-2 cursor-pointer shrink-0"
                >
                  <PenLine size={13} />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            {/* Personal Information Grid */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">
                    Personal Information
                  </h3>
                  <p className="text-xs text-slate-500">
                    Your contact and identification details
                  </p>
                </div>
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1.5 bg-red-50 hover:bg-red-100 px-3.5 py-1.5 rounded-xl transition-all cursor-pointer"
                >
                  <FaEdit size={12} />
                  <span>Update</span>
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                    <UserCheck size={18} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Full Name
                    </span>
                    <strong className="text-sm font-bold text-slate-900 block mt-0.5">
                      {userData?.customerName || "Not Provided"}
                    </strong>
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                    <Smartphone size={18} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Mobile Number
                    </span>
                    <strong className="text-sm font-bold text-slate-900 block mt-0.5">
                      {userData?.phoneNumber ? `+91 ${userData.phoneNumber}` : "Not Provided"}
                    </strong>
                  </div>
                </div>

                {/* Email Address */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Email Address
                    </span>
                    <strong className="text-sm font-bold text-slate-900 block mt-0.5 break-all">
                      {userData?.emailId || "Not Provided"}
                    </strong>
                  </div>
                </div>

                {/* Account Status */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Account Status
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-200 mt-1">
                      <FaCheckCircle size={11} /> Verified Active
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Diagnostic Promos */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/all-tests"
                className="bg-gradient-to-br from-red-50 to-white p-5 rounded-3xl border border-red-100 hover:border-red-300 shadow-xs hover:shadow-md transition-all flex items-center justify-between group"
              >
                <div>
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Book Diagnostic Test</span>
                  <h4 className="text-sm font-bold text-slate-900 mt-0.5">Explore 68+ Vital Lab Tests</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Free doorstep sample pickup</p>
                </div>
                <div className="w-10 h-10 rounded-2xl bg-red-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                  <ChevronRight size={16} />
                </div>
              </Link>

              <Link
                to="/all-packages"
                className="bg-gradient-to-br from-rose-50 to-white p-5 rounded-3xl border border-rose-100 hover:border-rose-300 shadow-xs hover:shadow-md transition-all flex items-center justify-between group"
              >
                <div>
                  <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">Full Body Checkups</span>
                  <h4 className="text-sm font-bold text-slate-900 mt-0.5">Preventive Health Packages</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Up to 70% Discount on Health Plans</p>
                </div>
                <div className="w-10 h-10 rounded-2xl bg-rose-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                  <ChevronRight size={16} />
                </div>
              </Link>
            </div>

          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/70 py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-start">

          {/* ===== LEFT SIDEBAR ===== */}
          <div className="lg:col-span-4 sticky top-24 self-start">
            <div className="bg-white border border-slate-200/90 rounded-3xl shadow-xs overflow-hidden">

              {/* USER PROFILE HEADER CARD */}
              <div className="p-6 text-center bg-gradient-to-b from-red-50/50 to-white border-b border-slate-100">
                <div className="relative inline-block mx-auto mb-3">
                  {getImageUrl() ? (
                    <img
                      src={getImageUrl()}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md ring-2 ring-red-200"
                      alt="profile"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-red-600 to-rose-500 flex items-center justify-center text-white text-3xl font-black shadow-md border-4 border-white ring-2 ring-red-200">
                      {userData.customerName?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                  )}

                  <button
                    onClick={() => setShowProfileModal(true)}
                    className="absolute bottom-0 right-0 w-7 h-7 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-md border-2 border-white cursor-pointer transition-all hover:scale-110"
                    title="Edit Avatar"
                  >
                    <PenLine size={12} />
                  </button>
                </div>

                <h3 className="text-base font-black text-slate-900 truncate">
                  {userData.customerName || "Valued Patient"}
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">
                  {userData?.phoneNumber ? `+91 ${userData.phoneNumber}` : userData?.emailId || "Registered User"}
                </p>

                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[11px] font-bold">
                  <FaCheckCircle size={10} />
                  <span>Verified Profile</span>
                </div>
              </div>

              {/* SIDEBAR NAVIGATION MENU */}
              <nav className="p-2 space-y-1">
                {menuItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        isActive
                          ? "bg-red-600 text-white shadow-md shadow-red-600/20"
                          : "text-slate-700 hover:bg-slate-50 hover:text-red-600"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={isActive ? "text-white" : "text-slate-400 group-hover:text-red-600"}>
                          {item.icon}
                        </span>
                        <span>{item.text}</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        {item.badge && !isActive && (
                          <span className="text-[10px] font-bold bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-100">
                            {item.badge}
                          </span>
                        )}
                        <FaChevronDown
                          size={10}
                          className={`-rotate-90 transition-transform ${
                            isActive ? "text-white" : "text-slate-300"
                          }`}
                        />
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* LOGOUT BUTTON */}
              <div className="p-3 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => setShowLogoutPopup(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white rounded-xl border border-red-200 transition-all duration-200 cursor-pointer"
                >
                  <FaSignOutAlt size={14} />
                  <span>Logout Account</span>
                </button>
              </div>

            </div>
          </div>

          {/* ===== RIGHT CONTENT PANEL ===== */}
          <div className="lg:col-span-8 min-w-0">
            {renderSection()}
          </div>

        </div>
      </div>

      {/* ===== MODALS ===== */}
      <UpdateProfileModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onSuccess={handleProfileUpdate}
      />

      <LogoutPopup
        isOpen={showLogoutPopup}
        onCancel={() => setShowLogoutPopup(false)}
        onConfirm={confirmLogout}
      />
    </div>
  );
}